import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FiCopy } from "react-icons/fi";
import toast from "react-hot-toast";
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";

const fetchOrder = async (id) => {
  const baseUrl = process.env.REACT_APP_APIURL;
  const res = await axios.get(`${baseUrl}api/order/order-details/${id}`);
  return res.data.data;
};

const updateOrderStatus = async ({ id, status }) => {
  const baseUrl = process.env.REACT_APP_APIURL;
  const res = await axios.put(`${baseUrl}api/order/order-status/${id}`, {
    status,
  });
  return res.data;
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAdmin] = useState(true); // You should replace this with your actual admin check

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when the page loads
  }, []);

  const {
    data: order,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["orderDetails", id],
    queryFn: () => fetchOrder(id),
    onSuccess: (data) => {
      // Redirect to orders page if the order is canceled
      if (data?.status?.toLowerCase() === "canceled") {
        toast.error("This order has been canceled");
        navigate("/orders");
      }
    }
  });

  const mutation = useMutation({
    mutationFn: ({ id, status }) => updateOrderStatus({ id, status }),
    onSuccess: () => {
      toast.success("Order status updated!");
      queryClient.invalidateQueries(["orderDetails", id]); // refetch order details
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  const handleCopy = () => {
    if (order?.orderId) {
      navigator.clipboard.writeText(order.orderId);
      toast.success("Order ID copied!");
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    mutation.mutate({ id, status: newStatus });
  };

  if (isLoading)
    return (
      <div>
        <Topbar />
        <Sidebar />
        <main id="main" className="main">
          <div
            className="text-center my-5"
            style={{
              minHeight: "60vh",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading...
          </div>
        </main>
      </div>
    );

  if (isError || !order)
    return (
      <div>
        <Topbar />
        <Sidebar />
        <main id="main" className="main">
          <div
            className="text-center my-5"
            style={{
              minHeight: "60vh",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Order not found
          </div>
        </main>
      </div>
    );

  const {
    orderId,
    customer,
    shippingAddress,
    payment,
    orderDate,
    notes,
    products,
    status, // ai bar fetch korechi updated status
    deliveryPriority
  } = order;

  const getStatusColor = (currentStatus) => {
    switch (currentStatus) {
      case "Delivered":
        return "green";
      case "Canceled":
        return "red";
      case "Shipped":
        return "blue"; // Using blue for Shipped
      case "Order Received":
        return "#0d6efd"; // Bootstrap primary blue
      case "Pending":
        return "#f2be00"; // Yellowish color for Pending
      default:
        return "#f2be00";
    }
  };

  return (
    <div>
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="container my-5">
          {/* Back Button */}
          <button
            onClick={() => navigate("/orders")}
            className="btn d-flex align-items-center gap-2 mb-4"
            style={{ color: "#0f4372" }}
          >
            <FaArrowLeftLong />
            <span className="fw-semibold">Back to Orders</span>
          </button>

          <h3 className="mb-4 fw-bold" style={{ color: "#0f4372" }}>
            Order Details
          </h3>

          <div className="row g-4 mb-5">
            {/* Customer Info */}
            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="text-primary">Customer Info</h5>
                  {customer?.name && <p className="mb-1">Name: {customer.name}</p>}
                  {customer?.email && <p className="mb-1">Email: {customer.email}</p>}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="text-primary">Shipping Address</h5>
                  {shippingAddress?.country && <p className="mb-1">Country: {shippingAddress.country}</p>}
                  {shippingAddress?.address && <p className="mb-1">Address: {shippingAddress.address}</p>}
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="text-primary">Payment Info</h5>
                  {payment?.method && <p className="mb-1">Method: {payment.method}</p>}
                  {payment?.paymentId && <p className="mb-1">Payment ID: {payment.paymentId}</p>}
                  {payment?.shippingAmount && <p className="mb-1">Shipping: £{payment.shippingAmount}</p>}
                  {payment?.total && <p className="mb-1">Total: £{payment.total}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="mb-4">
            <h5 className="fw-bold text-primary">Order Summary</h5>
            <p className="mb-1 d-flex align-items-center gap-2">
              Order ID: <span className="text-dark fw-semibold">{orderId}</span>
              <FiCopy
                size={16}
                className="text-primary cursor-pointer"
                role="button"
                onClick={handleCopy}
                title="Copy Order ID"
              />
            </p>
            <p className="mb-1">
              Status:{" "}
              <span className="fw-semibold" style={{ color: getStatusColor(status) }}>
                {status}
              </span>
            </p>
            <p className="mb-1">
              Order Date: {moment(orderDate).format("MMMM Do YYYY, h:mm A")}
            </p>
            {deliveryPriority && (
              <p className="mb-1">
                Delivery Priority: <span className="fw-semibold">{deliveryPriority}</span>
              </p>
            )}
            {notes && <p className="mb-1">Notes: {notes}</p>}

            {/* Status Dropdown (Admin only) */}
            {isAdmin && (
              <div className="mt-3">
                <label htmlFor="status" className="form-label">
                  Change Status
                </label>
                <select
                  id="status"
                  className="form-select"
                  defaultValue={status}
                  onChange={handleStatusChange}
                  disabled={mutation.isLoading} // loading thakle disable
                >
                  <option value="Order Received">Order Received</option>
                  <option value="Pending">Pending</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>
            )}
          </div>

          {/* Products */}
          <div>
            <h5 className="fw-bold text-primary mb-3">Products</h5>
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    <th>Attributes</th>
                    <th>Stock</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr key={index}>
                      <td>
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            style={{ height: "60px", objectFit: "cover" }}
                            className="rounded"
                          />
                        )}
                      </td>
                      <td>{product.name}</td>
                      <td>
                        £{product.price}
                        {product.reguler_price && product.reguler_price !== product.price && (
                          <s className="text-muted ms-2">£{product.reguler_price}</s>
                        )}
                      </td>
                      <td>{product.quantity}</td>
                      <td>
                        {product.selectedAttributes &&
                        Object.keys(product.selectedAttributes).length > 0 ? (
                          <ul className="mb-0 ps-3">
                            {Object.entries(product.selectedAttributes).map(
                              ([key, value]) =>
                                typeof value !== "object" && value && (
                                  <li key={key}>
                                    <strong>{key}:</strong> {value}
                                  </li>
                                )
                            )}
                          </ul>
                        ) : (
                          <span className="text-muted">No attributes</span>
                        )}
                      </td>
                      <td>{product.stock}</td>
                      <td
                        style={{
                          color: getStatusColor(product.status),
                        }}
                      >
                        {product.status || "Pending"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderDetails;
